import { useEffect, useState } from "react";
import { useGetContractModelsQuery } from "../api/workContractApiSlice";
import Loading from "components/commons/Loading";
import { useTranslation } from "react-i18next";
import WorkingWeek from "../../../test/WorkingWeek";

const DEFAULT_WEEK = [
    { day: "LUN", slots: [] },
    { day: "MAR", slots: [] },
    { day: "MER", slots: [] },
    { day: "GIO", slots: [] },
    { day: "VEN", slots: [] },
    { day: "SAB", slots: [] },
    { day: "DOM", slots: [] },
]

/**
 * Sezione "Contratto di lavoro" da usare sia in creazione che modifica dipendente.
 *
 * @param {object}   initialContract  - contratto esistente (edit) o null (create)
 * @param {function} onValueChanged   - callback chiamata ad ogni modifica: (payload) => void
 *                                     payload: { workerClass, employmentDate, week }
 */
export default function WorkerContractCard({
    initialContract = null,
    onValueChanged = () => { }
}) {
    const [t] = useTranslation(["global", "absenceReport"])

    // Stato locale: unica fonte di verità per i controlled input.
    // Inizializzato una sola volta da initialContract.
    const [workerClassGuid, setWorkerClassGuid] = useState(
        initialContract?.workerClass?.guid ?? null
    )
    const [employmentDate, setEmploymentDate] = useState(
        initialContract?.employmentDate ?? null
    )
    const [workingWeek, setWorkingWeek] = useState(
        initialContract?.week ?? DEFAULT_WEEK
    )

    const {
        data: contractModels,
        isLoading,
        isSuccess,
    } = useGetContractModelsQuery()

    const handleWorkerClassSelect = (e) => {
        setWorkerClassGuid(e.target.value || null)
    }

    const handleEmploymentDateChange = (e) => {
        const value = e.target.value
        if (!value) {
            setEmploymentDate(null)
            return
        }
        // new Date("YYYY-MM-DD") è affidabile — formato ISO garantito dall'input type="date"
        const date = new Date(value)
        setEmploymentDate(isNaN(date) ? null : value)
    }

    // Notifica il parent ogni volta che lo stato locale cambia
    useEffect(() => {
        if (!isSuccess) return

        const workerClass = contractModels
            .flatMap(cm => cm.workerClasses)
            .find(wc => wc.guid === workerClassGuid) ?? null

        onValueChanged({ workerClass, employmentDate, week: workingWeek })
    }, [workerClassGuid, employmentDate, workingWeek, isSuccess])

    if (isLoading) return <Loading />
    if (!isSuccess) return null

    const baseHours = workerClassGuid
        ? contractModels.find(cm =>
            cm.workerClasses.some(wc => wc.guid === workerClassGuid)
          )?.baseHours ?? null
        : null

    const fixedHours = workingWeek.reduce((total, day) =>
        total + day.slots.reduce((dayTotal, slot) =>
            dayTotal + (toMins(slot.end) - toMins(slot.start)) / 60, 0
        ), 0
    )

    return (
        <div className="row">
            <div className="row">
                <h4 className="mb-3">
                    <i className="fa-solid fa-briefcase"></i> {t("absenceReport:menu.work_contract")}
                </h4>

                <div className="col-5">
                    <div className="row">
                        <label htmlFor="employmentDate">{t("absenceReport:property.employment_date")}</label>
                        <div className="col-md-4 mb-3">
                            <input
                                className="date-picker"
                                type="date"
                                id="employmentDate"
                                value={employmentDate ?? ""}
                                onChange={handleEmploymentDateChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="col-md-6 mb-3">
                        <label>{t("absenceReport:property.contractual_level")}</label>
                        <select
                            className="form-select"
                            value={workerClassGuid ?? ""}
                            onChange={handleWorkerClassSelect}
                            required
                        >
                            <option value="">{t("absenceReport:helper.select_level")}</option>
                            {contractModels.map((contractModel, index) => (
                                <optgroup key={contractModel.guid ?? index} label={contractModel.name ?? ""}>
                                    {contractModel.workerClasses.map(workerClass => (
                                        <option key={workerClass.guid} value={workerClass.guid}>
                                            {workerClass.name}
                                        </option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>
                    </div>

                    <div className="row">
                        <label>{t("absenceReport:property.weekly_hours")}</label>
                        <div className="col-md-4 mb-3">
                            <h5>
                                <i className="fa-solid fa-clock mx-2"></i>
                                {fixedHours}
                            </h5>
                        </div>
                        {baseHours != null && (
                            <div className="col-md-4 mb-3 text-muted">
                                <span>
                                    <b>{Math.round((fixedHours / baseHours) * 100)}%</b>
                                    <br />
                                    <small>{t("absenceReport:helper.base_n_hours", { hours: baseHours })}</small>
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="col-7">
                    <label>{t("absenceReport:property.working_time")}</label>
                    <div className="card bg-dark p-3">
                        <WorkingWeek
                            workerWeek={workingWeek}
                            onWeekChange={setWorkingWeek}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

function toMins(time) {
    const [h, m] = time.split(":")
    return (+h * 60 + +m) || 0
}
